import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { LegalDisclaimer } from "@/context/DisclaimerContext";
import {
  ArrowLeft, ArrowRight, HandCoins, Loader2, AlertTriangle, FileText, Download,
  RefreshCw, Info, Check, Share2, Copy, Scale,
} from "lucide-react";
import DOMPurify from "dompurify";

function renderMarkdown(md) {
  const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const inline = (s) => DOMPurify.sanitize(esc(s).replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>").replace(/(?<!\*)\*(?!\*)(.+?)\*/g, "<em>$1</em>"));
  return (md || "").split("\n").map((line, i) => {
    const t = line.trim();
    if (!t) return <div key={i} className="h-3" />;
    if (t.startsWith("### ")) return <h3 key={i} className="font-heading font-semibold text-navy mt-3" dangerouslySetInnerHTML={{ __html: inline(t.slice(4)) }} />;
    if (t.startsWith("## ")) return <h2 key={i} className="font-heading font-semibold text-navy mt-3 text-lg" dangerouslySetInnerHTML={{ __html: inline(t.slice(3)) }} />;
    if (t.startsWith("# ")) return <h1 key={i} className="font-heading font-bold text-navy mt-3 text-xl" dangerouslySetInnerHTML={{ __html: inline(t.slice(2)) }} />;
    return <p key={i} className="text-sm text-[#334155] leading-relaxed" dangerouslySetInnerHTML={{ __html: inline(t) }} />;
  });
}

function toPlainText(md) {
  return (md || "")
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/\*\*(.+?)\*\*/g, "$1")
    .replace(/(?<!\*)\*(?!\*)(.+?)\*/g, "$1")
    .trim();
}

export default function ReturnRequest() {
  const { id: propertyId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [prop, setProp] = useState(null);
  const [reportLink, setReportLink] = useState(null);
  const [letters, setLetters] = useState([]);
  const [step, setStep] = useState(1);

  const [form, setForm] = useState({ move_out_date: "", deposit_amount: "", return_by_date: "" });
  const [genState, setGenState] = useState("idle"); // idle | pending | error
  const [genError, setGenError] = useState("");
  const [doc, setDoc] = useState(null);
  const [markdown, setMarkdown] = useState("");
  const [saveState, setSaveState] = useState("saved");
  const saveTimer = useRef(null);
  const [pdfState, setPdfState] = useState("idle");
  const [shareUrl, setShareUrl] = useState(null);

  const loadLetters = useCallback(async () => {
    const { data } = await api.get(`/dispute-letters/${propertyId}?letter_type=return_request`);
    setLetters(data);
  }, [propertyId]);

  const load = useCallback(async () => {
    const { data: p } = await api.get(`/properties/${propertyId}`);
    setProp(p);
    setForm((f) => ({
      ...f,
      deposit_amount: p.deposit_amount != null ? String(p.deposit_amount) : "",
      move_out_date: (p.move_out_date || "").slice(0, 10),
    }));
    try {
      const { data: links } = await api.get(`/share-links/${propertyId}`);
      const active = (links || []).find((l) => l.link_type === "report" && l.status === "active");
      if (active) setReportLink(`${window.location.origin}/report/${active.slug}`);
    } catch (e) { console.error("load share links failed", e); }
    await loadLetters();
    setLoading(false);
  }, [propertyId, loadLetters]);

  useEffect(() => { load(); }, [load]);

  const dep = parseFloat(form.deposit_amount);
  const step1Valid = form.move_out_date && form.return_by_date && dep > 0;

  const generate = async () => {
    setStep(2);
    setGenState("pending");
    setGenError("");
    try {
      const { data } = await api.post(`/properties/${propertyId}/return-request`, {
        move_out_date: form.move_out_date,
        deposit_amount: dep,
        return_by_date: form.return_by_date,
        report_link: reportLink,
      });
      setDoc(data);
      setMarkdown(data.letter_body_markdown);
      setShareUrl(null);
      setGenState("idle");
      loadLetters();
    } catch (e) {
      setGenError(e?.response?.data?.detail || "Could not draft the letter.");
      setGenState("error");
    }
  };

  const scheduleSave = (value) => {
    setMarkdown(value);
    setSaveState("saving");
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.put(`/dispute-letters/id/${doc.letter_id}`, { letter_body_markdown: value });
        setDoc(data); setShareUrl(null); setSaveState("saved");
      } catch { setSaveState("saved"); }
    }, 800);
  };

  const regenerate = async () => {
    if (!window.confirm("Regenerate will overwrite your manual edits. Continue?")) return;
    await generate();
  };

  const generatePdf = async () => {
    setPdfState("pending");
    try {
      const { data } = await api.post(`/dispute-letters/id/${doc.letter_id}/pdf`);
      setDoc(data);
      loadLetters();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not assemble the PDF.");
    } finally {
      setPdfState("idle");
    }
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/dispute-letters/id/${doc.letter_id}/pdf-file`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "deposit-return-request.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(toPlainText(markdown));
      toast.success("Letter copied — paste it into an email.");
    } catch { toast.error("Copy failed"); }
  };

  const share = async () => {
    try {
      const { data } = await api.post(`/dispute-letters/id/${doc.letter_id}/share`);
      const url = `${window.location.origin}${data.share_path}`;
      setShareUrl(url);
      try { await navigator.clipboard.writeText(url); toast.success("Share link copied"); } catch { toast.success("Share link ready"); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create link"); }
  };

  const startNew = () => { setDoc(null); setMarkdown(""); setShareUrl(null); setGenState("idle"); setStep(1); };

  if (loading) return <AppShell><Skeleton className="h-96 rounded-2xl" /></AppShell>;

  return (
    <AppShell>
      <button data-testid="rr-back-btn" onClick={() => navigate(`/property/${propertyId}`)} className="flex items-center gap-2 text-sm text-[#475569] hover:text-navy mb-4">
        <ArrowLeft size={16} /> Back to property
      </button>
      <div className="flex items-center gap-2">
        <HandCoins size={24} className="text-brand" />
        <h1 className="font-heading text-3xl font-bold tracking-tight text-navy">Request deposit return</h1>
      </div>

      {/* Guidance line */}
      <div className="mt-3 flex items-start gap-2 rounded-xl border border-[#BFDBFE] bg-[#EFF6FF] px-4 py-3 text-sm text-[#1E40AF]" data-testid="rr-guidance">
        <Info size={16} className="mt-0.5 shrink-0" />
        <span>Start here. If your landlord doesn't respond or withholds unfairly, generate a <button onClick={() => navigate(`/properties/${propertyId}/dispute-letter`)} className="underline font-medium inline-flex items-center gap-1"><Scale size={13} /> dispute letter</button> next.</span>
      </div>

      {/* Prior letters */}
      {letters.length > 0 && (
        <div className="mt-5 rounded-2xl border border-[#E2E8F0] bg-white p-4" data-testid="rr-letters-list">
          <div className="flex items-center justify-between">
            <h2 className="font-heading font-semibold text-navy">Your requests</h2>
            <Button data-testid="rr-start-new-btn" size="sm" variant="outline" onClick={startNew} className="rounded-lg">Start new request</Button>
          </div>
          <div className="mt-3 divide-y divide-[#F1F5F9]">
            {letters.map((l) => (
              <div key={l.letter_id} className="flex items-center justify-between py-2 text-sm" data-testid={`rr-letter-row-${l.letter_id}`}>
                <span className="text-[#475569]">{new Date(l.created_at).toLocaleDateString()} · <span className={l.status === "finalized" ? "text-[#177245] font-medium" : "text-[#92400E]"}>{l.status}</span></span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Stepper */}
      <div className="mt-6 flex items-center gap-2" data-testid="rr-steps">
        {["Details", "Draft", "Export"].map((s, i) => (
          <div key={s} className={`flex items-center gap-2 ${i + 1 === step ? "text-navy font-semibold" : "text-[#94A3B8]"}`}>
            <span className={`grid place-items-center h-6 w-6 rounded-full text-xs font-bold ${i + 1 <= step ? "bg-brand text-white" : "bg-[#E2E8F0] text-[#94A3B8]"}`}>{i + 1}</span>
            <span className="text-sm hidden sm:inline">{s}</span>
            {i < 2 && <ArrowRight size={14} className="text-[#CBD5E1]" />}
          </div>
        ))}
      </div>

      {/* STEP 1 — Details */}
      {step === 1 && (
        <div className="mt-6 max-w-2xl" data-testid="rr-step-details">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium text-navy">Move-out date</Label>
              <Input data-testid="rr-moveout-date" type="date" value={form.move_out_date} onChange={(e) => setForm({ ...form, move_out_date: e.target.value })} className="mt-1.5 h-12 rounded-xl" />
            </div>
            <div>
              <Label className="text-sm font-medium text-navy">Deposit amount ($)</Label>
              <Input data-testid="rr-deposit" type="number" value={form.deposit_amount} onChange={(e) => setForm({ ...form, deposit_amount: e.target.value })} className="mt-1.5 h-12 rounded-xl" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-sm font-medium text-navy">Please return by</Label>
              <Input data-testid="rr-returnby-date" type="date" value={form.return_by_date} onChange={(e) => setForm({ ...form, return_by_date: e.target.value })} className="mt-1.5 h-12 rounded-xl" />
            </div>
          </div>
          {reportLink && (
            <p className="mt-3 text-xs text-[#64748B]" data-testid="rr-report-link-note">Your shared condition report will be referenced in the letter.</p>
          )}
          <Button data-testid="rr-generate-btn" disabled={!step1Valid} onClick={generate} className="mt-6 rounded-xl h-12 px-8 bg-brand hover:bg-brand-hover text-white disabled:opacity-50">
            Draft letter <ArrowRight size={16} className="ml-2" />
          </Button>
        </div>
      )}

      {/* STEP 2 — Draft */}
      {step === 2 && (
        <div className="mt-6" data-testid="rr-step-draft">
          <div className="rounded-xl border border-amber-border bg-amber-soft/60 px-4 py-3 text-sm text-[#8A5A12]" data-testid="rr-legal-notice">
            <div className="flex items-start gap-2">
              <Info size={16} className="mt-0.5 shrink-0" />
              <LegalDisclaimer keyName="dispute_letter" className="text-sm text-[#8A5A12]" />
            </div>
          </div>

          {genState === "pending" && (
            <div className="mt-4" data-testid="rr-draft-skeleton">
              <p className="text-[#475569] mb-3 flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Drafting your letter… (typically 5–15 seconds)</p>
              <Skeleton className="h-80 rounded-xl" />
            </div>
          )}

          {genState === "error" && (
            <div className="mt-4 rounded-2xl border border-[#FECACA] bg-[#FEF2F2] p-5" data-testid="rr-draft-error">
              <p className="text-sm font-medium text-[#B91C1C] flex items-center gap-2"><AlertTriangle size={16} /> {genError}</p>
              <Button data-testid="rr-draft-retry-btn" onClick={generate} className="mt-3 rounded-xl bg-navy hover:bg-navy-hover text-white">Try again</Button>
            </div>
          )}

          {genState === "idle" && doc && (
            <>
              <div className="mt-4 flex items-center justify-between">
                <span className="text-xs text-[#94A3B8]" data-testid="rr-save-indicator">{saveState === "saving" ? "Saving…" : "Saved"}</span>
                <Button data-testid="rr-regenerate-btn" size="sm" variant="outline" onClick={regenerate} className="rounded-lg"><RefreshCw size={14} className="mr-1.5" /> Regenerate</Button>
              </div>
              <div className="mt-3 hidden md:grid grid-cols-2 gap-4">
                <Textarea data-testid="rr-editor" value={markdown} onChange={(e) => scheduleSave(e.target.value)} className="min-h-[460px] rounded-xl font-mono text-xs leading-relaxed" />
                <div className="min-h-[460px] rounded-xl border border-[#E2E8F0] bg-white p-5 overflow-y-auto" data-testid="rr-preview">{renderMarkdown(markdown)}</div>
              </div>
              <Tabs defaultValue="edit" className="mt-3 md:hidden">
                <TabsList><TabsTrigger value="edit">Edit</TabsTrigger><TabsTrigger value="preview">Preview</TabsTrigger></TabsList>
                <TabsContent value="edit"><Textarea data-testid="rr-editor-mobile" value={markdown} onChange={(e) => scheduleSave(e.target.value)} className="min-h-[420px] rounded-xl font-mono text-xs" /></TabsContent>
                <TabsContent value="preview"><div className="min-h-[420px] rounded-xl border border-[#E2E8F0] bg-white p-4">{renderMarkdown(markdown)}</div></TabsContent>
              </Tabs>
              <div className="mt-6 flex gap-3">
                <Button data-testid="rr-step2-back-btn" variant="outline" onClick={() => setStep(1)} className="rounded-xl h-12"><ArrowLeft size={16} className="mr-2" /> Back</Button>
                <Button data-testid="rr-step2-next-btn" onClick={() => setStep(3)} className="rounded-xl h-12 px-8 bg-brand hover:bg-brand-hover text-white">Continue to export <ArrowRight size={16} className="ml-2" /></Button>
              </div>
            </>
          )}
        </div>
      )}

      {/* STEP 3 — Export */}
      {step === 3 && doc && (
        <div className="mt-6 max-w-2xl" data-testid="rr-step-export">
          <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6">
            <div className="flex flex-wrap gap-3">
              <Button data-testid="rr-copy-email-btn" variant="outline" onClick={copyEmail} className="rounded-xl h-12"><Copy size={18} className="mr-2" /> Copy as email text</Button>
              {doc.status === "finalized" && doc.pdf_storage_url ? (
                <>
                  <Button data-testid="rr-download-pdf-btn" onClick={downloadPdf} className="rounded-xl h-12 bg-navy hover:bg-navy-hover text-white"><Download size={18} className="mr-2" /> Download PDF</Button>
                  <Button data-testid="rr-share-btn" variant="outline" onClick={share} className="rounded-xl h-12"><Share2 size={18} className="mr-2" /> Share read-only link</Button>
                  <Button data-testid="rr-regenerate-pdf-btn" variant="ghost" onClick={generatePdf} disabled={pdfState === "pending"} className="rounded-xl h-12 text-brand"><RefreshCw size={16} className="mr-2" /> Regenerate</Button>
                </>
              ) : (
                <Button data-testid="rr-generate-pdf-btn" onClick={generatePdf} disabled={pdfState === "pending"} className="rounded-xl h-12 px-6 bg-brand hover:bg-brand-hover text-white">
                  {pdfState === "pending" ? <><Loader2 size={18} className="mr-2 animate-spin" /> Assembling PDF…</> : <><FileText size={18} className="mr-2" /> Export PDF</>}
                </Button>
              )}
            </div>
            {shareUrl && (
              <div className="mt-4 flex items-center gap-2 rounded-xl bg-[#FAF7F2] border border-[#E2E8F0] p-3" data-testid="rr-share-url-box">
                <input readOnly value={shareUrl} className="flex-1 bg-transparent text-sm text-[#475569] outline-none" />
                <button onClick={() => { navigator.clipboard.writeText(shareUrl); toast.success("Copied"); }} className="text-brand"><Copy size={16} /></button>
              </div>
            )}
            <ul className="mt-5 space-y-1.5 text-sm text-[#475569]" data-testid="rr-checklist">
              <li className="flex items-center gap-2"><Check size={14} className="text-[#177245]" /> Add your name and address where marked before sending.</li>
              <li className="flex items-center gap-2"><Check size={14} className="text-[#177245]" /> Keep a copy for your records.</li>
              <li className="flex items-center gap-2"><Check size={14} className="text-[#177245]" /> Give your landlord a reasonable window to respond.</li>
            </ul>
          </div>
          <div className="mt-6"><Button data-testid="rr-step3-back-btn" variant="outline" onClick={() => setStep(2)} className="rounded-xl h-12"><ArrowLeft size={16} className="mr-2" /> Back to editor</Button></div>
        </div>
      )}
    </AppShell>
  );
}
